import prettyBytes from 'pretty-bytes';

import { isDefined, isTruthy } from './types.js';

export function validateFileExtension(props: {
  allowedExtensions: string[];
  fileExtension: string;
}) {
  const { allowedExtensions, fileExtension } = props;

  if (
    !allowedExtensions?.some(
      (extension) => extension.toLowerCase() === fileExtension?.toLowerCase()
    )
  ) {
    return (
      'File not an accepted format. Upload a file with one of the following extensions: ' +
      allowedExtensions?.join(', ')
    );
  }
}

export function isImageFile(props: { mimeType?: string; fileExtension?: string }) {
  const { mimeType, fileExtension } = props;
  if (isTruthy(mimeType)) {
    return mimeType.startsWith('image/');
  }
  return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(
    fileExtension?.toLowerCase() ?? ''
  );
}

export function formatFileSize(bytes?: number) {
  if (!isDefined(bytes)) {
    return 'Unknown';
  }

  return prettyBytes(bytes, {
    binary: true,
    minimumFractionDigits: 1,
    maximumFractionDigits: 2,
  });
}

interface GetAndValidateFirstFileFromFileListProps {
  files: FileList;
  validContentTypes: string[];
  maxFileSizeInBytes: number;
}

/**
 * Gets the first file from a file list and validates it.
 *
 * @param files The file list to validate.
 * @param validContentTypes The valid content types.
 * @param maxFileSizeInBytes The maximum file size in bytes.
 * @returns The first file if it meets the criteria, or undefined if it does not.
 */
export function getAndValidateFirstFileFromFileList(
  props: GetAndValidateFirstFileFromFileListProps
): File {
  const { files, validContentTypes, maxFileSizeInBytes } = props;
  if (!isTruthy(files.length)) {
    throw new Error('No files provided');
  }
  const file = files.item(0);
  if (!isDefined(file)) {
    throw new Error('No file provided');
  }
  const fileExtensionError = validateFileExtension({
    allowedExtensions: validContentTypes,
    fileExtension: file.type,
  });
  if (isTruthy(fileExtensionError)) {
    throw new Error(fileExtensionError);
  }
  if (file.size > maxFileSizeInBytes) {
    throw new Error(`File size must be less than ${formatFileSize(maxFileSizeInBytes)}`);
  }
  return file;
}
